import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert, AvatarGroup, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid, InputAdornment, MenuItem,
  Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from "@mui/material";
import { FolderKanban, LayoutGrid, List, Plus, Search, Archive, Users, AlertTriangle, Timer, Trash2, DatabaseZap } from "lucide-react";
import { EmptyState, GlassCard, HoverCard, ListSkeleton, PageHeader, PersonAvatar, T, TYPE, formatDate, formatRelative, primaryButtonSx, ghostButtonSx, labelSx } from "../user/complaints/shared";
import { pm, errorMessage } from "./api";
import { PmPage, usePm } from "./PmContext";
import { ColorChip, ProjectBadge, Progress } from "./ui";
import { PersonPicker, Select, fieldSx } from "./inputs";

const VISIBILITY = [
  { value: "private", label: "Private — members only" },
  { value: "team", label: "Team — members of the selected team" },
  { value: "shared", label: "Shared — everyone in the workspace can browse" },
  { value: "internal", label: "Internal — all staff (not client roles)" },
];
const STATUS_COLOR = { Planning: "#7A5AF8", Active: "#12B76A", "On Hold": "#F79009", Completed: "#2E90FA", Cancelled: "#98A2B3" };

const suggestKey = (name) => {
  const words = String(name).toUpperCase().replace(/[^A-Z0-9 ]/g, "").split(/\s+/).filter(Boolean);
  const k = words.length > 1 ? words.map((w) => w[0]).join("") : (words[0] || "").slice(0, 4);
  return /^[A-Z]/.test(k) ? k.slice(0, 6) : `P${k}`.slice(0, 6);
};

export const CreateProjectDialog = ({ open, onClose, onCreated }) => {
  const ctx = usePm();
  const [form, setForm] = useState({});
  const [keyTouched, setKeyTouched] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      setForm({ name: "", key: "", description: "", templateId: ctx.templates.find((t) => t.key === "software")?._id || ctx.templates[0]?._id, visibility: "private", leadId: String(ctx.me.id), managerId: null, teamId: null, members: [], startDate: "", endDate: "", status: ctx.settings.projectStatuses[1] || ctx.settings.projectStatuses[0] });
      setKeyTouched(false);
      setError("");
    }
    // reset only when the dialog opens: ctx changes on reload (e.g. after creating a team) and must not wipe the form
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const [newTeam, setNewTeam] = useState(null);
  const [teamSaving, setTeamSaving] = useState(false);
  useEffect(() => { if (open) setNewTeam(null); }, [open]);
  const createTeam = async () => {
    setTeamSaving(true);
    setError("");
    try {
      const t = await pm.createTeam({ name: newTeam.name, members: newTeam.members });
      await ctx.reload();
      setForm((f) => ({ ...f, teamId: t._id, visibility: "team" }));
      setNewTeam(null);
      ctx.notify(`Team “${t.name}” created and selected`);
    } catch (err) {
      setError(errorMessage(err, "Could not create the team"));
    } finally {
      setTeamSaving(false);
    }
  };
  const template = ctx.templates.find((t) => t._id === form.templateId);
  const submit = async () => {
    setSaving(true);
    setError("");
    try {
      const p = await pm.createProject({ ...form, typeId: form.typeId || template?.projectTypeId || undefined, members: form.members.map((id) => ({ memberId: id })), startDate: form.startDate || undefined, endDate: form.endDate || undefined, teamId: form.teamId || undefined, managerId: form.managerId || undefined });
      ctx.notify(`Project ${p.key} created`);
      onCreated(p);
    } catch (err) {
      setError(errorMessage(err, "Could not create the project"));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: T.radiusCard } }}>
      <DialogTitle sx={{ fontWeight: 700 }}>Create project</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography sx={{ ...labelSx, mb: 1 }}>Template</Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" }, gap: 1.25, mb: 2.5 }}>
          {ctx.templates.map((t) => (
            <GlassCard key={t._id} onClick={() => set("templateId")(t._id)} sx={{ p: 1.5, cursor: "pointer", boxShadow: "none", borderColor: form.templateId === t._id ? T.brand : T.border, bgcolor: form.templateId === t._id ? T.brandSoft : "#fff" }}>
              <Typography sx={{ fontWeight: 700, fontSize: TYPE.small }}>{t.name}</Typography>
              <Typography sx={{ fontSize: TYPE.micro, color: T.inkSecondary, mt: 0.25 }}>{t.description}</Typography>
              <Stack direction="row" spacing={0.5} sx={{ mt: 0.75 }}>
                <Chip size="small" label={t.board?.type || "board"} sx={{ height: 18, fontSize: "0.64rem" }} />
                <Chip size="small" label={t.estimation} sx={{ height: 18, fontSize: "0.64rem" }} />
              </Stack>
            </GlassCard>
          ))}
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={8}>
            <TextField fullWidth size="small" label="Project name *" value={form.name || ""} autoFocus sx={fieldSx}
              onChange={(e) => { set("name")(e.target.value); if (!keyTouched) set("key")(suggestKey(e.target.value)); }} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" label="Key *" value={form.key || ""} helperText="Prefix of issue keys, e.g. JWK-12" sx={fieldSx}
              onChange={(e) => { setKeyTouched(true); set("key")(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10)); }} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth multiline minRows={2} size="small" label="Description" value={form.description || ""} onChange={(e) => set("description")(e.target.value)} sx={fieldSx} />
          </Grid>
          <Grid item xs={12} sm={6}><Select label="Project type" value={form.typeId || template?.projectTypeId} onChange={set("typeId")} options={ctx.projectTypes.map((t) => ({ value: t._id, label: t.name }))} /></Grid>
          <Grid item xs={12} sm={6}><Select label="Status" value={form.status} onChange={set("status")} options={ctx.settings.projectStatuses.map((s) => ({ value: s, label: s }))} /></Grid>
          <Grid item xs={12} sm={6}><PersonPicker label="Project lead" value={form.leadId} onChange={set("leadId")} allowEmpty={false} /></Grid>
          <Grid item xs={12} sm={6}><PersonPicker label="Project manager" value={form.managerId} onChange={set("managerId")} /></Grid>
          <Grid item xs={12} sm={6}><Select label="Visibility" value={form.visibility} onChange={set("visibility")} options={VISIBILITY.filter((v) => v.value !== "internal" || ctx.settings.allowInternalVisibility)} /></Grid>
          <Grid item xs={12} sm={6}><Select label="Team" value={form.teamId} onChange={set("teamId")} allowEmpty options={ctx.teams.map((t) => ({ value: t._id, label: t.name }))} helperText={form.visibility === "team" && !form.teamId ? "Choose the team that can see this project" : ""} /></Grid>
          {ctx.canCreateProject && (
            <Grid item xs={12}>
              {!newTeam ? (
                <Button size="small" startIcon={<Plus size={14} />} onClick={() => setNewTeam({ name: "", members: [] })}>Create a new team</Button>
              ) : (
                <Box sx={{ border: `1px solid ${T.border}`, borderRadius: 2, p: 1.5 }}>
                  <Typography sx={{ ...labelSx, mb: 1 }}>New team</Typography>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={5}><TextField fullWidth size="small" label="Team name *" value={newTeam.name} onChange={(e) => setNewTeam({ ...newTeam, name: e.target.value })} sx={fieldSx} /></Grid>
                    <Grid item xs={12} sm={7}><PersonPicker label="Team members" multiple value={newTeam.members} onChange={(members) => setNewTeam({ ...newTeam, members })} /></Grid>
                  </Grid>
                  <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1.25 }}>
                    <Button size="small" onClick={() => setNewTeam(null)}>Cancel</Button>
                    <Button size="small" variant="contained" disabled={teamSaving || !newTeam.name.trim()} onClick={createTeam}>{teamSaving ? "Creating…" : "Create team"}</Button>
                  </Stack>
                </Box>
              )}
            </Grid>
          )}
          <Grid item xs={12}><PersonPicker label="Members (added as Developer; change roles later)" multiple value={form.members} onChange={set("members")} /></Grid>
          <Grid item xs={6}><TextField fullWidth size="small" type="date" label="Start date" InputLabelProps={{ shrink: true }} value={form.startDate} onChange={(e) => set("startDate")(e.target.value)} sx={fieldSx} /></Grid>
          <Grid item xs={6}><TextField fullWidth size="small" type="date" label="End date" InputLabelProps={{ shrink: true }} value={form.endDate} onChange={(e) => set("endDate")(e.target.value)} sx={fieldSx} /></Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        {(() => {
          const why = !form.name?.trim() ? "Enter a project name" : !form.key ? "Enter a project key" : form.visibility === "team" && !form.teamId ? "Select a team (or change Visibility)" : "";
          return why ? <Typography sx={{ fontSize: TYPE.micro, color: "#B42318", mr: "auto" }}>{why}</Typography> : null;
        })()}
        <Button variant="outlined" sx={ghostButtonSx} onClick={onClose}>Cancel</Button>
        <Button variant="contained" sx={primaryButtonSx} disabled={saving || !form.name?.trim() || !form.key || (form.visibility === "team" && !form.teamId)} onClick={submit}>{saving ? "Creating…" : "Create project"}</Button>
      </DialogActions>
    </Dialog>
  );
};

const ProjectCard = ({ p, onOpen }) => (
  <HoverCard sx={{ p: 2, cursor: "pointer", height: "100%", display: "flex", flexDirection: "column" }} onClick={onOpen}>
    <Stack direction="row" spacing={1.5} alignItems="center">
      <ProjectBadge project={p} size={38} />
      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
        <Typography noWrap sx={{ fontWeight: 700, fontSize: TYPE.body }}>{p.name}</Typography>
        <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>{p.key} · {p.visibility}</Typography>
      </Box>
      <ColorChip label={p.archivedAt ? "Archived" : p.status} color={p.archivedAt ? "#98A2B3" : STATUS_COLOR[p.status]} />
    </Stack>
    {p.description && <Typography sx={{ fontSize: TYPE.small, color: T.inkSecondary, mt: 1.25, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{String(p.description).replace(/<[^>]+>/g, " ")}</Typography>}
    <Box sx={{ flexGrow: 1 }} />
    <Box sx={{ mt: 1.5 }}><Progress value={p.stats.progress} label={`${p.stats.done}/${p.stats.total} issues done`} /></Box>
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1.5 }}>
      <AvatarGroup max={5} sx={{ "& .MuiAvatar-root": { width: 24, height: 24, fontSize: 10, border: "2px solid #fff" } }}>
        {p.members.map((m) => <PersonAvatar key={String(m.id)} name={m.name} size={24} />)}
      </AvatarGroup>
      <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>{p.memberCount} members</Typography>
      <Box sx={{ flexGrow: 1 }} />
      {p.stats.overdue > 0 && <Tooltip title="Overdue issues"><Chip size="small" icon={<AlertTriangle size={12} />} label={p.stats.overdue} sx={{ height: 20, bgcolor: "#FEE4E2", color: "#B42318", fontSize: "0.68rem" }} /></Tooltip>}
      {p.stats.activeSprint && <Tooltip title={`Active: ${p.stats.activeSprint.name}`}><Chip size="small" icon={<Timer size={12} />} label="Sprint" sx={{ height: 20, fontSize: "0.68rem" }} /></Tooltip>}
    </Stack>
    <Typography sx={{ fontSize: "0.68rem", color: T.inkMuted, mt: 1 }}>Lead {p.lead?.name} · updated {formatRelative(p.lastActivityAt || p.updatedAt)}</Typography>
  </HoverCard>
);

const Directory = () => {
  const ctx = usePm();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [view, setView] = useState(() => {
    try {
      return localStorage.getItem("pm.projects.view") || "grid";
    } catch {
      return "grid";
    }
  });
  const [createOpen, setCreateOpen] = useState(params.get("create") === "1");
  const filters = { q: params.get("q") || "", status: params.get("status") || "", type: params.get("type") || "", lead: params.get("lead") || "", team: params.get("team") || "", visibility: params.get("visibility") || "", archived: params.get("archived") || "", sort: params.get("sort") || "activity" };
  const setFilter = (k, v) => {
    const n = new URLSearchParams(params);
    if (v) n.set(k, v);
    else n.delete(k);
    setParams(n, { replace: true });
  };
  const load = useCallback(async () => {
    try {
      const p = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      setRows(await pm.projects(p));
      setError("");
    } catch (err) {
      setError(errorMessage(err));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);
  useEffect(() => {
    const t = setTimeout(load, filters.q ? 250 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);
  const changeView = (v) => {
    if (!v) return;
    setView(v);
    try {
      localStorage.setItem("pm.projects.view", v);
    } catch {
      /* per-viewer convenience only */
    }
  };
  // A lone project with no filters applied: open it directly (New project lives in the top bar).
  const unfiltered = !params.toString();
  useEffect(() => {
    if (unfiltered && rows?.length === 1) navigate(`/projects/${rows[0].key}`, { replace: true });
  }, [rows, unfiltered, navigate]);
  const counts = useMemo(() => ({ total: rows?.length || 0, active: rows?.filter((r) => r.status === "Active").length || 0 }), [rows]);

  return (
    <>
      <PageHeader
        icon={FolderKanban}
        title="Shared Projects"
        subtitle={`Access, monitor, and collaborate on active workspace projects · ${counts.total} total (${counts.active} active)`}
        actions={(
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<DatabaseZap size={16} />} sx={ghostButtonSx} onClick={() => navigate("/pm/import")}>Import data</Button>
            {ctx.canCreateProject && <Button variant="contained" startIcon={<Plus size={16} />} sx={primaryButtonSx} onClick={() => setCreateOpen(true)}>Create project</Button>}
          </Stack>
        )}
      />
      <Stack direction={{ xs: "column", md: "row" }} spacing={1.25} sx={{ mb: 2 }} alignItems={{ md: "center" }} flexWrap="wrap" useFlexGap>
        <TextField size="small" placeholder="Search name, key, lead…" value={filters.q} onChange={(e) => setFilter("q", e.target.value)} sx={{ ...fieldSx, minWidth: 240 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search size={15} /></InputAdornment> }} />
        <TextField select size="small" label="Status" value={filters.status} onChange={(e) => setFilter("status", e.target.value)} sx={{ ...fieldSx, minWidth: 130 }}>
          <MenuItem value="">All</MenuItem>{ctx.settings.projectStatuses.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Type" value={filters.type} onChange={(e) => setFilter("type", e.target.value)} sx={{ ...fieldSx, minWidth: 150 }}>
          <MenuItem value="">All</MenuItem>{ctx.projectTypes.map((t) => <MenuItem key={t._id} value={t._id}>{t.name}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Lead" value={filters.lead} onChange={(e) => setFilter("lead", e.target.value)} sx={{ ...fieldSx, minWidth: 150 }}>
          <MenuItem value="">Anyone</MenuItem>{ctx.people.map((p) => <MenuItem key={String(p.id)} value={String(p.id)}>{p.name}</MenuItem>)}
        </TextField>
        {ctx.teams.length > 0 && (
          <TextField select size="small" label="Team" value={filters.team} onChange={(e) => setFilter("team", e.target.value)} sx={{ ...fieldSx, minWidth: 130 }}>
            <MenuItem value="">All</MenuItem>{ctx.teams.map((t) => <MenuItem key={t._id} value={t._id}>{t.name}</MenuItem>)}
          </TextField>
        )}
        <TextField select size="small" label="Visibility" value={filters.visibility} onChange={(e) => setFilter("visibility", e.target.value)} sx={{ ...fieldSx, minWidth: 130 }}>
          <MenuItem value="">All</MenuItem>{VISIBILITY.map((v) => <MenuItem key={v.value} value={v.value}>{v.value}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Sort" value={filters.sort} onChange={(e) => setFilter("sort", e.target.value)} sx={{ ...fieldSx, minWidth: 140 }}>
          <MenuItem value="activity">Recent activity</MenuItem><MenuItem value="name">Name</MenuItem><MenuItem value="key">Key</MenuItem><MenuItem value="created">Newest</MenuItem>
        </TextField>
        <Chip icon={<Archive size={13} />} label="Archived" variant={filters.archived ? "filled" : "outlined"} onClick={() => setFilter("archived", filters.archived ? "" : "only")} />
        {ctx.me.isAdmin && <Chip icon={<Trash2 size={13} />} label="Trash" variant="outlined" onClick={() => navigate("/pm/settings?section=trash")} />}
        <Box sx={{ flexGrow: 1 }} />
        <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, v) => changeView(v)}>
          <ToggleButton value="grid"><LayoutGrid size={15} /></ToggleButton>
          <ToggleButton value="list"><List size={15} /></ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {!rows || (unfiltered && rows.length === 1) ? (
        <ListSkeleton rows={4} height={120} />
      ) : !rows.length ? (
        <GlassCard>
          <EmptyState icon={FolderKanban} title={filters.q || filters.status ? "No projects match these filters" : "No projects yet"}
            description="Projects hold issues, boards, sprints and releases. Start from a template — you can change everything later."
            action={ctx.canCreateProject && <Button variant="contained" sx={primaryButtonSx} startIcon={<Plus size={16} />} onClick={() => setCreateOpen(true)}>Create project</Button>} />
        </GlassCard>
      ) : view === "grid" ? (
        <Grid container spacing={2}>
          {rows.map((p) => (
            <Grid item key={p._id} xs={12} sm={6} lg={4} xl={3}><ProjectCard p={p} onOpen={() => navigate(`/projects/${p.key}`)} /></Grid>
          ))}
        </Grid>
      ) : (
        <GlassCard sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>{["Project", "Status", "Lead", "Members", "Progress", "Overdue", "Dates", "Updated"].map((h) => <TableCell key={h} sx={labelSx}>{h}</TableCell>)}</TableRow>
            </TableHead>
            <TableBody>
              {rows.map((p) => (
                <TableRow key={p._id} hover sx={{ cursor: "pointer" }} onClick={() => navigate(`/projects/${p.key}`)}>
                  <TableCell><Stack direction="row" spacing={1.25} alignItems="center"><ProjectBadge project={p} size={28} /><Box><Typography sx={{ fontWeight: 600, fontSize: TYPE.small }}>{p.name}</Typography><Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>{p.key}</Typography></Box></Stack></TableCell>
                  <TableCell><ColorChip label={p.archivedAt ? "Archived" : p.status} color={STATUS_COLOR[p.status]} /></TableCell>
                  <TableCell sx={{ fontSize: TYPE.small }}>{p.lead?.name}</TableCell>
                  <TableCell><Stack direction="row" spacing={0.5} alignItems="center"><Users size={13} /><span>{p.memberCount}</span></Stack></TableCell>
                  <TableCell sx={{ minWidth: 140 }}><Progress value={p.stats.progress} /></TableCell>
                  <TableCell sx={{ color: p.stats.overdue ? "#B42318" : T.inkMuted }}>{p.stats.overdue}</TableCell>
                  <TableCell sx={{ fontSize: TYPE.micro, whiteSpace: "nowrap" }}>{p.startDate ? formatDate(p.startDate) : "—"} → {p.endDate ? formatDate(p.endDate) : "—"}</TableCell>
                  <TableCell sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>{formatRelative(p.lastActivityAt || p.updatedAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </GlassCard>
      )}
      <CreateProjectDialog open={createOpen && ctx.canCreateProject} onClose={() => { setCreateOpen(false); setFilter("create", ""); }} onCreated={(p) => navigate(`/projects/${p.key}`)} />
    </>
  );
};

const ProjectsHome = () => (
  <PmPage>
    <Directory />
  </PmPage>
);

export default ProjectsHome;
